import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, Users, CheckCircle, XCircle,
  MapPin, Wrench, Eye, Clock, Star, Download, FileCheck, FileX, Search, Trash2, ExternalLink,
  Briefcase, CreditCard, Crown, MessageCircle, RotateCcw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Etiquetas legibles para servicios y medios de pago (claves guardadas en el backend)
const SERVICE_LABELS = {
  plomeria: 'Plomería',
  electricidad: 'Electricidad',
  gasista: 'Gasista',
  pintura: 'Pintura',
  cerrajeria: 'Cerrajería',
  aire_acondicionado: 'Aire acondicionado',
  arreglos_generales: 'Arreglos Generales',
  jardineria: 'Jardinería',
  techista: 'Techista',
};

const PAYMENT_LABELS = {
  efectivo: 'Efectivo',
  transferencia: 'Transferencia bancaria',
  mercado_pago: 'Mercado Pago',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

// Muestra el perfil profesional del proveedor: servicios, medios de pago e interés en PRO
function ProfileSection({ provider }) {
  const services = provider?.services || [];
  const payments = provider?.payment_methods || [];
  const proInterest = Boolean(provider?.pro_interest);

  const formatProDate = (dateString) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };
  const proDate = formatProDate(provider?.pro_interest_at);

  return (
    <div className="border border-gray-200 rounded-xl p-4" data-testid="provider-profile-section">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Briefcase className="w-4 h-4 text-orange-500" />
        Perfil Profesional
        {proInterest && (
          <span className="ml-auto flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            <Crown className="w-3 h-3" />
            Interés PRO
          </span>
        )}
      </h3>

      <div className="space-y-3">
        <div>
          <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            <Wrench className="w-3.5 h-3.5" /> Servicios ofrecidos
          </p>
          {services.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {services.map((s) => (
                <span key={s} className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
                  {SERVICE_LABELS[s] || s}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sin servicios seleccionados</p>
          )}
        </div>

        <div>
          <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            <CreditCard className="w-3.5 h-3.5" /> Medios de pago
          </p>
          {payments.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {payments.map((m) => (
                <span key={m} className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                  {PAYMENT_LABELS[m] || m}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Sin medios de pago seleccionados</p>
          )}
        </div>

        {/* Interés en la suscripción Providers PRO */}
        <div className="pt-1 border-t border-gray-100">
          <p className="text-xs text-gray-500 mb-1.5 flex items-center gap-1.5">
            <Crown className="w-3.5 h-3.5" /> Suscripción Providers PRO
          </p>
          {proInterest ? (
            <div className="flex items-center gap-2" data-testid="pro-interest-detail">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                Interesado en PRO
              </span>
              {proDate && <span className="text-xs text-gray-400">desde {proDate}</span>}
            </div>
          ) : (
            <p className="text-sm text-gray-400" data-testid="pro-interest-detail">Sin interés registrado</p>
          )}
        </div>
      </div>
    </div>
  );
}

// Muestra el origen de campaña (UTM) de un ticket o proveedor
function UtmSection({ data }) {
  const hasUtm = data?.utm_source || data?.utm_medium || data?.utm_campaign;
  return (
    <div className="border border-gray-200 rounded-xl p-4" data-testid="utm-section">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <ExternalLink className="w-4 h-4 text-orange-500" />
        Origen / Campaña
      </h3>
      {hasUtm ? (
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Campaña</p>
            <p className="text-sm font-medium break-words">{data.utm_campaign || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Fuente</p>
            <p className="text-sm font-medium break-words">{data.utm_source || '—'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Medio</p>
            <p className="text-sm font-medium break-words">{data.utm_medium || '—'}</p>
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-400 text-center py-2">&nbsp;</p>
      )}
    </div>
  );
}

const WA_STAGE_LABELS = { day_2: 'Día 2', day_6: 'Día 6', day_10: 'Día 10' };
const WA_FLOW_LABELS = {
  awaiting_initial: 'Esperando respuesta',
  awaiting_provider: 'Eligiendo profesional',
  awaiting_rating: 'Esperando calificación',
  awaiting_feedback: 'Esperando comentario',
  awaiting_other_reason: 'Motivo (otro medio)',
  awaiting_no_longer_reason: 'Clasificando cierre',
  awaiting_cancellation_reason: 'Motivo de cancelación',
  done: 'Finalizado',
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [kpis, setKpis] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [providers, setProviders] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [ticketEvents, setTicketEvents] = useState([]);
  const [adminNote, setAdminNote] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deletingTicket, setDeletingTicket] = useState(false);
  const [sendingFollowup, setSendingFollowup] = useState(false);
  const [resettingFollowup, setResettingFollowup] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [providerRatings, setProviderRatings] = useState([]);
  const [loadingProviderRatings, setLoadingProviderRatings] = useState(false);
  const [providerAccess, setProviderAccess] = useState(true);
  const [togglingAccess, setTogglingAccess] = useState(false);
  const [ticketCreation, setTicketCreation] = useState(true);
  const [togglingCreation, setTogglingCreation] = useState(false);

  // ---- Provider filters ----
  const [filterSort, setFilterSort] = useState('newest');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDocs, setFilterDocs] = useState('all');
  const [filterSearch, setFilterSearch] = useState('');

  const filteredProviders = useMemo(() => {
    let result = [...providers];

    // Sort by date
    result.sort((a, b) =>
      filterSort === 'newest'
        ? new Date(b.created_at) - new Date(a.created_at)
        : new Date(a.created_at) - new Date(b.created_at)
    );

    // Status filter
    if (filterStatus === 'approved') result = result.filter(p => p.is_approved && !p.is_blocked);
    else if (filterStatus === 'pending') result = result.filter(p => !p.is_approved && !p.is_blocked);
    else if (filterStatus === 'blocked') result = result.filter(p => p.is_blocked);

    // Docs filter
    if (filterDocs === 'complete') result = result.filter(p => p.verification_documents_completed);
    else if (filterDocs === 'incomplete') result = result.filter(p => !p.verification_documents_completed);

    // Search (name / email / phone)
    if (filterSearch.trim()) {
      const q = filterSearch.toLowerCase().trim();
      result = result.filter(p =>
        p.username.toLowerCase().includes(q) ||
        p.email.toLowerCase().includes(q) ||
        (p.phone || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [providers, filterSort, filterStatus, filterDocs, filterSearch]);

  const providerMetrics = useMemo(() => ({
    total: filteredProviders.length,
    approved: filteredProviders.filter(p => p.is_approved && !p.is_blocked).length,
    pending: filteredProviders.filter(p => !p.is_approved && !p.is_blocked).length,
    blocked: filteredProviders.filter(p => p.is_blocked).length,
    docsComplete: filteredProviders.filter(p => p.verification_documents_completed).length,
    docsIncomplete: filteredProviders.filter(p => !p.verification_documents_completed).length,
    proInterest: filteredProviders.filter(p => p.pro_interest).length,
  }), [filteredProviders]);

  const hasActiveFilters = filterStatus !== 'all' || filterDocs !== 'all' || filterSearch.trim() !== '' || filterSort !== 'newest';

  const resetFilters = () => {
    setFilterSort('newest');
    setFilterStatus('all');
    setFilterDocs('all');
    setFilterSearch('');
  };

  const authHeader = () => {
    const credentials = btoa(`${username}:${password}`);
    return { Authorization: `Basic ${credentials}` };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: authHeader()
      });
      setAuthenticated(true);
      setKpis(response.data);
      fetchTickets();
      fetchProviders();
      fetchProviderAccess();
      fetchTicketCreation();
      toast.success('Acceso autorizado');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/admin/tickets`, {
        headers: authHeader()
      });
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      toast.error('Error al cargar tickets');
    }
  };

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/admin/providers`, {
        headers: authHeader()
      });
      setProviders(response.data);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Error al cargar proveedores');
    }
  };

  const fetchProviderAccess = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/provider-access`, {
        headers: authHeader()
      });
      setProviderAccess(response.data.provider_access_enabled);
    } catch {}
  };

  const handleToggleAccess = async () => {
    setTogglingAccess(true);
    try {
      const newValue = !providerAccess;
      await axios.patch(`${API}/admin/settings/provider-access`, { enabled: newValue }, {
        headers: authHeader()
      });
      setProviderAccess(newValue);
      toast.success(newValue ? 'Acceso de proveedores activado' : 'Acceso de proveedores desactivado');
    } catch {
      toast.error('Error al cambiar el acceso');
    } finally {
      setTogglingAccess(false);
    }
  };

  const fetchTicketCreation = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/ticket-creation`, {
        headers: authHeader()
      });
      setTicketCreation(response.data.ticket_creation_enabled);
    } catch {}
  };

  const handleToggleCreation = async () => {
    setTogglingCreation(true);
    try {
      const newValue = !ticketCreation;
      await axios.patch(`${API}/admin/settings/ticket-creation`, { enabled: newValue }, {
        headers: authHeader()
      });
      setTicketCreation(newValue);
      toast.success(newValue ? 'Creación de tickets activada' : 'Creación de tickets desactivada');
    } catch {
      toast.error('Error al cambiar la creación de tickets');
    } finally {
      setTogglingCreation(false);
    }
  };

  const handleApproveProvider = async (provider) => {
    try {
      await axios.patch(`${API}/admin/providers/${provider.id}/approve`, {}, {
        headers: authHeader()
      });
      toast.success(`${provider.username} aprobado`);
      fetchProviders();
    } catch {
      toast.error('Error al aprobar el proveedor');
    }
  };

  const handleToggleBlockProvider = async (provider) => {
    const newBlocked = !provider.is_blocked;
    try {
      await axios.patch(`${API}/admin/providers/${provider.id}/block`, { is_blocked: newBlocked }, {
        headers: authHeader()
      });
      toast.success(newBlocked ? `${provider.username} bloqueado` : `${provider.username} desbloqueado`);
      fetchProviders();
    } catch {
      toast.error('Error al cambiar el estado del proveedor');
    }
  };

  const handleViewProvider = async (provider) => {
    setSelectedProvider(provider);
    setShowProviderModal(true);
    setLoadingProviderRatings(true);
    try {
      const response = await axios.get(`${API}/admin/providers/${provider.id}/ratings`, {
        headers: authHeader()
      });
      setProviderRatings(response.data);
    } catch {
      toast.error('Error al cargar calificaciones');
      setProviderRatings([]);
    } finally {
      setLoadingProviderRatings(false);
    }
  };

  const downloadDoc = async (providerId, docType, label) => {
    try {
      const response = await fetch(`${API}/admin/providers/${providerId}/documents/${docType}`, {
        headers: authHeader()
      });
      if (!response.ok) {
        toast.error('Documento no encontrado');
        return;
      }
      const blob = await response.blob();

      // Determinar extensión desde el MIME type real del archivo
      const extMap = {
        'image/jpeg': '.jpg', 'image/jpg': '.jpg', 'image/png': '.png',
        'image/webp': '.webp', 'image/heic': '.heic', 'image/heif': '.heif',
        'application/pdf': '.pdf',
      };
      const ext = extMap[blob.type] || '';

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}_${providerId}${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Documento descargado');
    } catch {
      toast.error('Error al descargar el documento');
    }
  };

  const fetchTicketEvents = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/admin/tickets/${ticketId}/events`, {
        headers: authHeader()
      });
      setTicketEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Error al cargar eventos');
    }
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    await fetchTicketEvents(ticket.id);
    setShowTicketModal(true);
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.patch(`${API}/admin/tickets/status`, {
        ticket_id: selectedTicket.id,
        new_status: newStatus,
        admin_note: adminNote || null
      }, {
        headers: authHeader()
      });
      
      toast.success('Estado actualizado');
      setAdminNote('');
      fetchTickets();
      setShowTicketModal(false);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error al actualizar estado');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const reloadTicketsAndSelected = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/admin/tickets`, { headers: authHeader() });
      setTickets(response.data);
      if (ticketId) {
        const updated = (response.data || []).find((t) => t.id === ticketId);
        if (updated) setSelectedTicket(updated);
      }
    } catch (error) {
      console.error('Error reloading tickets:', error);
    }
  };

  const handleSendFollowup = async () => {
    if (!selectedTicket) return;
    setSendingFollowup(true);
    try {
      const res = await axios.post(
        `${API}/admin/tickets/${selectedTicket.id}/whatsapp-followup`,
        {},
        { headers: authHeader() }
      );
      const stage = WA_STAGE_LABELS[res.data?.stage] || '';
      toast.success(`Seguimiento de WhatsApp enviado${stage ? ` (${stage})` : ''}`);
      fetchTicketEvents(selectedTicket.id);
      reloadTicketsAndSelected(selectedTicket.id);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error al enviar el seguimiento de WhatsApp');
    } finally {
      setSendingFollowup(false);
    }
  };

  const handleResetFollowup = async () => {
    if (!selectedTicket) return;
    setResettingFollowup(true);
    try {
      await axios.post(
        `${API}/admin/tickets/${selectedTicket.id}/whatsapp-followup/reset`,
        {},
        { headers: authHeader() }
      );
      toast.success('Seguimiento de WhatsApp reiniciado');
      fetchTicketEvents(selectedTicket.id);
      reloadTicketsAndSelected(selectedTicket.id);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Error al reiniciar el seguimiento');
    } finally {
      setResettingFollowup(false);
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;
    setDeletingTicket(true);
    try {
      await axios.delete(`${API}/admin/tickets/${ticketToDelete.id}`, {
        headers: authHeader()
      });
      toast.success('Ticket eliminado');
      if (selectedTicket?.id === ticketToDelete.id) {
        setShowTicketModal(false);
      }
      setTicketToDelete(null);
      fetchTickets();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      toast.error('Error al eliminar el ticket');
    } finally {
      setDeletingTicket(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatDateShort = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('es-AR', {
        timeZone: 'America/Argentina/Buenos_Aires',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Publicado': 'bg-blue-100 text-blue-700',
      'Concretado': 'bg-green-100 text-green-700',
      'Expirado': 'bg-red-100 text-red-700',
      'Cancelado': 'bg-gray-100 text-gray-700',
      'Resuelto por fuera de Providers': 'bg-purple-100 text-purple-700',
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-strong)' }}>
                Admin Dashboard
              </h1>
              <p className="text-gray-600">Ingresá tus credenciales</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <Input
                  id="username"
                  data-testid="admin-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  data-testid="admin-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <Button
                data-testid="admin-login-btn"
                type="submit"
                className="w-full"
                style={{ backgroundColor: 'var(--primary-strong)' }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                data-testid="back-to-home-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Dashboard screen
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white px-4 py-5 border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-strong)' }}>
            Admin Dashboard
          </h1>

          <div className="flex items-center gap-4 flex-wrap">
            {/* Toggle global de acceso */}
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2">
              <span className="text-sm font-medium text-gray-700">Acceso de proveedores</span>
              <button
                data-testid="provider-access-toggle"
                onClick={handleToggleAccess}
                disabled={togglingAccess}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${providerAccess ? 'bg-green-500' : 'bg-gray-300'}`}
                aria-label="Toggle acceso proveedores"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${providerAccess ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
              <span className={`text-xs font-semibold ${providerAccess ? 'text-green-600' : 'text-gray-400'}`}>
                {providerAccess ? 'ON' : 'OFF'}
              </span>
            </div>

            {/* Toggle global de creación de tickets */}
            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2">
              <span className="text-sm font-medium text-gray-700">Creación de tickets</span>
              <button
                data-testid="ticket-creation-toggle"
                onClick={handleToggleCreation}
                disabled={togglingCreation}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${ticketCreation ? 'bg-green-500' : 'bg-gray-300'}`}
                aria-label="Toggle creación de tickets"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${ticketCreation ? 'translate-x-6' : 'translate-x-1'}`}
                />
              </button>
              <span className={`text-xs font-semibold ${ticketCreation ? 'text-green-600' : 'text-gray-400'}`}>
                {ticketCreation ? 'ON' : 'OFF'}
              </span>
            </div>

            <Button
              variant="ghost"
              onClick={() => {
                setAuthenticated(false);
                setUsername('');
                setPassword('');
                navigate('/');
              }}
              data-testid="logout-btn"
            >
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      {/* KPIs */}
      {kpis && (
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--dark-primary)' }}>
            Métricas Clave
          </h2>
          
          {/* Tickets count */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500">Publicados</p>
                <Clock className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-3xl font-bold text-blue-600">
                {kpis.totals.publicados}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500">Concretados</p>
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-3xl font-bold text-green-600">
                {kpis.totals.concretados}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500">Cancelados</p>
                <XCircle className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--primary-strong)' }}>
                {kpis.totals.cancelados}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500">Resueltos por fuera</p>
                <ExternalLink className="w-5 h-5 text-purple-400" />
              </div>
              <p className="text-3xl font-bold text-purple-600">
                {kpis.totals.resueltos_fuera}
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-500">Total</p>
                <Users className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--primary-strong)' }}>
                {kpis.totals.total}
              </p>
            </div>
          </div>

          {/* Performance */}
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <p className="text-sm text-gray-500 mb-2">Contactos Revelados</p>
              <p className="text-2xl font-bold text-blue-600">
                {kpis.engagement.total_reveals}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                total de reveals en la plataforma
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <p className="text-sm text-gray-500 mb-2">Tiempo hasta Concreción</p>
              <p className="text-2xl font-bold text-green-600">
                {kpis.performance.avg_lifetime_concretado_hours.toFixed(1)} h
              </p>
              <p className="text-xs text-gray-500 mt-1">
                promedio desde publicación
              </p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <p className="text-sm text-gray-500 mb-2">Tiempo hasta Primer Reveal</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--primary-strong)' }}>
                {kpis.performance.avg_time_to_first_reveal_hours.toFixed(1)} h
              </p>
              <p className="text-xs text-gray-500 mt-1">
                desde publicación hasta primer contacto
              </p>
            </div>
          </div>

          {/* Distribution */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{ color: 'var(--primary-strong)' }} />
                Distribución por Zona
              </h3>
              <div className="space-y-2">
                {kpis.distribution.by_zone.map((item) => (
                  <div key={item.zone} className="flex items-center justify-between py-2">
                    <span className="text-sm">{item.zone}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Wrench className="w-5 h-5" style={{ color: 'var(--primary-strong)' }} />
                Distribución por Categoría
              </h3>
              <div className="space-y-2">
                {kpis.distribution.by_category.slice(0, 6).map((item) => (
                  <div key={item.category} className="flex items-center justify-between py-2">
                    <span className="text-sm">{item.category}</span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Tickets Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold" style={{ color: 'var(--dark-primary)' }}>
                Todos los Tickets ({tickets.length})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zona</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Categoría</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{ticket.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{ticket.name}</td>
                      <td className="px-4 py-3 text-sm">{ticket.zone}</td>
                      <td className="px-4 py-3 text-sm">{ticket.category}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(ticket.status)}`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(ticket.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Button
                            data-testid={`view-ticket-${ticket.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-ticket-${ticket.id}`}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                            onClick={() => setTicketToDelete(ticket)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Providers Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mt-6" data-testid="providers-section">

            {/* Header */}
            <div className="p-6 border-b flex items-center gap-3">
              <Users className="w-5 h-5" style={{ color: 'var(--primary-strong)' }} />
              <h2 className="text-xl font-bold" style={{ color: 'var(--dark-primary)' }}>
                Proveedores Registrados
              </h2>
              <span className="ml-auto text-sm text-gray-400">
                {hasActiveFilters
                  ? `${filteredProviders.length} de ${providers.length}`
                  : providers.length}
              </span>
            </div>

            {/* Filter bar */}
            <div className="p-4 border-b bg-gray-50 space-y-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, email o teléfono..."
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white"
                  data-testid="provider-search"
                />
              </div>
              {/* Selects row */}
              <div className="flex flex-wrap gap-2 items-center">
                <Select value={filterSort} onValueChange={setFilterSort}>
                  <SelectTrigger className="w-auto h-8 text-xs gap-1.5" data-testid="filter-sort">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Más recientes primero</SelectItem>
                    <SelectItem value="oldest">Más antiguos primero</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-auto h-8 text-xs gap-1.5" data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los estados</SelectItem>
                    <SelectItem value="approved">Aprobados</SelectItem>
                    <SelectItem value="pending">Pendientes</SelectItem>
                    <SelectItem value="blocked">Bloqueados</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={filterDocs} onValueChange={setFilterDocs}>
                  <SelectTrigger className="w-auto h-8 text-xs gap-1.5" data-testid="filter-docs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toda la documentación</SelectItem>
                    <SelectItem value="complete">Docs completa</SelectItem>
                    <SelectItem value="incomplete">Docs incompleta</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <button
                    data-testid="filter-reset"
                    onClick={resetFilters}
                    className="px-3 h-8 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 transition-colors"
                  >
                    Limpiar filtros
                  </button>
                )}
              </div>
            </div>

            {/* Quick metrics */}
            <div className="px-4 py-3 border-b bg-white flex flex-wrap gap-2" data-testid="provider-metrics">
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
                Total: {providerMetrics.total}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                Aprobados: {providerMetrics.approved}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium">
                Pendientes: {providerMetrics.pending}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                Bloqueados: {providerMetrics.blocked}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                Docs completas: {providerMetrics.docsComplete}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-xs font-medium">
                Sin docs: {providerMetrics.docsIncomplete}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                Interés PRO: {providerMetrics.proInterest}
              </span>
            </div>

            {providers.length === 0 ? (
              <div className="px-6 py-10 text-center text-gray-400 text-sm">
                No hay proveedores registrados todavía
              </div>
            ) : filteredProviders.length === 0 ? (
              <div className="px-6 py-10 text-center space-y-2">
                <p className="text-gray-500 text-sm font-medium">Sin resultados para los filtros aplicados</p>
                <button
                  onClick={resetFilters}
                  className="text-xs text-orange-500 hover:underline"
                >
                  Limpiar filtros
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Docs</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating ⭐</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trabajos</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contactos</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registro</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredProviders.map((provider) => (
                      <tr key={provider.id} className="hover:bg-gray-50" data-testid={`provider-row-${provider.id}`}>
                        {/* Avatar + username */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: provider.avatar_color }}
                            >
                              {provider.username.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-medium">{provider.username}</span>
                            {provider.pro_interest && (
                              <span
                                title="Dejó interés en Providers PRO"
                                data-testid={`pro-badge-${provider.id}`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700"
                              >
                                <Crown className="w-3 h-3" />
                                PRO
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{provider.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{provider.phone || '—'}</td>
                        {/* Estado */}
                        <td className="px-4 py-3">
                          {provider.is_blocked ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">Bloqueado</span>
                          ) : provider.is_approved ? (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">Activo</span>
                          ) : (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">Pendiente</span>
                          )}
                        </td>
                        {/* Docs */}
                        <td className="px-4 py-3">
                          {provider.verification_documents_completed ? (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                              <FileCheck className="w-3 h-3" />
                              Verificado
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              <FileX className="w-3 h-3" />
                              Sin docs
                            </span>
                          )}
                        </td>
                        {/* Rating */}
                        <td className="px-4 py-3 text-sm">
                          {provider.rating_avg !== null ? (
                            <span className="font-semibold text-yellow-600">
                              {provider.rating_avg} ★
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">Sin calificaciones</span>
                          )}
                        </td>
                        {/* Trabajos (calificaciones) */}
                        <td className="px-4 py-3 text-sm text-center font-medium">
                          {provider.ratings_count}
                        </td>
                        {/* Contactos revelados */}
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`font-bold ${provider.reveals_count > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                            {provider.reveals_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDateShort(provider.created_at)}
                        </td>
                        {/* Acciones */}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {!provider.is_approved && !provider.is_blocked && (
                              <Button
                                data-testid={`approve-provider-${provider.id}`}
                                size="sm"
                                className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => handleApproveProvider(provider)}
                              >
                                Aprobar
                              </Button>
                            )}
                            <Button
                              data-testid={`block-provider-${provider.id}`}
                              size="sm"
                              variant="outline"
                              className={`h-7 px-2 text-xs ${provider.is_blocked ? 'border-green-500 text-green-600 hover:bg-green-50' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
                              onClick={() => handleToggleBlockProvider(provider)}
                            >
                              {provider.is_blocked ? 'Desbloquear' : 'Bloquear'}
                            </Button>
                            <Button
                              data-testid={`view-provider-${provider.id}`}
                              size="sm"
                              variant="outline"
                              className="h-7 px-2"
                              onClick={() => handleViewProvider(provider)}
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      <Dialog open={showTicketModal} onOpenChange={setShowTicketModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-modal">
          <DialogHeader>
            <DialogTitle>Detalle del Ticket</DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-6">
              {/* Basic info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Nombre</p>
                  <p className="font-medium">{selectedTicket.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <p className="font-medium">{selectedTicket.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Teléfono</p>
                  <p className="font-medium">{selectedTicket.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Estado</p>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedTicket.status)}`}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Zona</p>
                  <p className="font-medium">{selectedTicket.zone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Categoría</p>
                  <p className="font-medium">{selectedTicket.category}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500 mb-2">Descripción</p>
                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedTicket.description}</p>
              </div>

              {/* Campaign attribution */}
              <UtmSection data={selectedTicket} />

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Revelaciones</p>
                  <p className="text-2xl font-bold text-green-600">{selectedTicket.reveal_count}</p>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Eventos</p>
                  <p className="text-2xl font-bold text-purple-600">{ticketEvents.length}</p>
                </div>
              </div>

              {/* WhatsApp follow-up */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-green-600" />
                  Seguimiento WhatsApp
                </h3>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Mensajes enviados</p>
                    <p className="font-medium">{selectedTicket.whatsapp_followup_count ?? 0} / 3</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Última etapa</p>
                    <p className="font-medium">{WA_STAGE_LABELS[selectedTicket.whatsapp_followup_stage] || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Estado de la conversación</p>
                    <p className="font-medium">{WA_FLOW_LABELS[selectedTicket.whatsapp_flow_state] || 'Sin iniciar'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">¿Respondió?</p>
                    <p className="font-medium">{selectedTicket.whatsapp_replied ? 'Sí' : 'No'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Último envío</p>
                    <p className="font-medium">{selectedTicket.last_whatsapp_at ? formatDate(selectedTicket.last_whatsapp_at) : '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Próximo envío</p>
                    <p className="font-medium">{selectedTicket.next_whatsapp_at ? formatDate(selectedTicket.next_whatsapp_at) : '—'}</p>
                  </div>
                  {selectedTicket.resolution_source && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Resolución</p>
                      <p className="font-medium">{selectedTicket.resolution_source === 'PROVIDERS' ? 'Vía Providers' : 'Por otro medio'}</p>
                    </div>
                  )}
                  {selectedTicket.other_resolution_reason && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Motivo (otro medio)</p>
                      <p className="font-medium">{selectedTicket.other_resolution_reason}</p>
                    </div>
                  )}
                  {selectedTicket.cancellation_reason && (
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Motivo de cancelación</p>
                      <p className="font-medium">{selectedTicket.cancellation_reason}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    data-testid="send-followup-btn"
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-green-700 border-green-300 hover:bg-green-50"
                    onClick={handleSendFollowup}
                    disabled={sendingFollowup || resettingFollowup}
                  >
                    {sendingFollowup
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <MessageCircle className="w-3.5 h-3.5" />}
                    Enviar seguimiento ahora
                  </Button>
                  <Button
                    data-testid="reset-followup-btn"
                    size="sm"
                    variant="outline"
                    className="gap-1.5 text-gray-700"
                    onClick={handleResetFollowup}
                    disabled={resettingFollowup || sendingFollowup}
                  >
                    {resettingFollowup
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RotateCcw className="w-3.5 h-3.5" />}
                    Reiniciar seguimiento
                  </Button>
                </div>
              </div>

              {/* Events timeline */}
              <div>
                <h3 className="font-semibold mb-3">Historial de Eventos</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {ticketEvents.map((event, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg text-sm">
                      <div className="flex-1">
                        <p className="font-medium">{event.event_type}</p>
                        <p className="text-xs text-gray-500">{formatDate(event.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Update status */}
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Actualizar Estado</h3>
                <div className="space-y-3">
                  <Select
                    onValueChange={(value) => handleUpdateStatus(value)}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger data-testid="admin-status-select">
                      <SelectValue placeholder="Seleccionar nuevo estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Publicado">Publicado</SelectItem>
                      <SelectItem value="Concretado">Concretado</SelectItem>
                      <SelectItem value="Cancelado">Cancelado</SelectItem>
                      <SelectItem value="Resuelto por fuera de Providers">Resuelto por fuera de Providers</SelectItem>
                    </SelectContent>
                  </Select>

                  <div>
                    <Label htmlFor="admin-note">Nota interna (opcional)</Label>
                    <Textarea
                      id="admin-note"
                      data-testid="admin-note-input"
                      placeholder="Agregar nota..."
                      value={adminNote}
                      onChange={(e) => setAdminNote(e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Delete Ticket Confirmation */}
      <AlertDialog open={!!ticketToDelete} onOpenChange={(open) => { if (!open) setTicketToDelete(null); }}>
        <AlertDialogContent data-testid="delete-ticket-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar ticket</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción es permanente y no se puede deshacer. Se eliminarán el ticket
              {ticketToDelete ? ` de ${ticketToDelete.name}` : ''} y todos sus datos asociados
              (eventos, notas y calificaciones).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingTicket}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-ticket"
              onClick={(e) => { e.preventDefault(); handleDeleteTicket(); }}
              disabled={deletingTicket}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deletingTicket ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Provider Detail Modal */}
      <Dialog open={showProviderModal} onOpenChange={setShowProviderModal}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="provider-detail-modal">
          <DialogHeader>
            <DialogTitle>Detalle del Proveedor</DialogTitle>
          </DialogHeader>

          {selectedProvider && (
            <div className="space-y-5">
              {/* Provider info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold flex-shrink-0"
                  style={{ backgroundColor: selectedProvider.avatar_color }}
                >
                  {selectedProvider.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{selectedProvider.username}</p>
                  <p className="text-sm text-gray-500">{selectedProvider.email}</p>
                  <p className="text-xs text-gray-400 mt-0.5">Registrado: {formatDateShort(selectedProvider.created_at)}</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Rating</p>
                  <p className="text-xl font-bold text-yellow-600">
                    {selectedProvider.rating_avg !== null ? `${selectedProvider.rating_avg} ★` : '—'}
                  </p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Trabajos</p>
                  <p className="text-xl font-bold text-green-600">{selectedProvider.ratings_count}</p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Contactos</p>
                  <p className="text-xl font-bold text-blue-600">{selectedProvider.reveals_count}</p>
                </div>
              </div>

              {/* Perfil profesional: servicios, medios de pago e interés PRO */}
              <ProfileSection provider={selectedProvider} />

              {/* Campaign attribution */}
              <UtmSection data={selectedProvider} />

              {/* Documentos de verificación */}
              <div className="border border-gray-200 rounded-xl p-4">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-blue-500" />
                  Documentos de Verificación
                  {selectedProvider.verification_documents_completed ? (
                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">Completo</span>
                  ) : (
                    <span className="ml-auto px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Sin documentos</span>
                  )}
                </h3>
                {selectedProvider.verification_documents_completed ? (
                  <div className="space-y-2">
                    {[
                      { type: 'dni_front', label: 'DNI Frente', file: 'dni_frente' },
                      { type: 'dni_back', label: 'DNI Dorso', file: 'dni_dorso' },
                      { type: 'criminal_record', label: 'Antecedentes Penales', file: 'antecedentes' },
                    ].map(({ type, label, file }) => (
                      <div key={type} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">{label}</span>
                        <button
                          data-testid={`download-doc-${type}-${selectedProvider.id}`}
                          onClick={() => downloadDoc(selectedProvider.id, type, file)}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" />
                          Descargar
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-2">
                    Este proveedor no ha subido documentos todavía.
                  </p>
                )}
              </div>

              {/* Ratings list */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" />
                  Calificaciones recibidas
                </h3>

                {loadingProviderRatings ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--primary-strong)' }} />
                  </div>
                ) : providerRatings.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">Sin calificaciones todavía</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {providerRatings.map((rating, idx) => (
                      <div key={idx} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg text-sm">
                        <div>
                          <span className="font-semibold text-yellow-600">{rating.stars} ★</span>
                          {rating.tags && rating.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {rating.tags.map((tag, i) => (
                                <span key={i} className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded-full text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                          {formatDateShort(rating.created_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
